import {
  type Amount,
  applyRateToAmount,
  divideByNumber,
  financialRateToNumber,
  multiplyByRate,
  newFinancialRate,
  zeroAmount,
} from '@orus.eu/amount'
import type { PaymentRecurrence } from '@orus.eu/quote'

import type { InsuranceOutput } from '../insurance-output.ts'

export const monthsInAYear = 12

export type InsuranceOutputAmountOptions =
  | {
      // Describe the base premium with taxes, without any fees nor discounts applied.
      type: 'basePremiumWithTaxes'
    }
  | {
      type: 'insuredExTax'
    }
  | {
      type: 'taxes'
    }
  | {
      type: 'partnerManagementFee'
    }
  | {
      type: 'orusManagementFee'
    }
  | {
      type: 'orusInstallmentFee'
    }
  | {
      type: 'orusDiscountAmount'
    }
  | {
      type: 'overchargeAmount'
    }

export const quoteDeclinations = ['withoutAnyDiscount', 'final', 'unique'] as const

export type QuoteDeclination = (typeof quoteDeclinations)[number]

export function getPaymentRecurrenceInsuranceOutputAmount(
  insuranceOutput: InsuranceOutput,
  paymentRecurrence: PaymentRecurrence,
  options?: InsuranceOutputAmountOptions,
): Amount {
  switch (paymentRecurrence) {
    case 'monthly': {
      return divideByNumber(getInsuranceOutputAmount(insuranceOutput, options), monthsInAYear)
    }
    case 'yearly': {
      return getInsuranceOutputAmount(insuranceOutput, options)
    }
  }
}

export function getInsuranceOutputAmount(
  insuranceOutput: InsuranceOutput,
  options?: InsuranceOutputAmountOptions,
): Amount {
  let amount = insuranceOutput.yearlyExTax

  // If there are no fees and rates, we apply the tax rate to the ex tax amount and early exit
  if (!insuranceOutput.feesAndRates || options?.type === 'basePremiumWithTaxes') {
    return applyRateToAmount(amount, insuranceOutput.taxRate)
  }

  const applicableFeesAndRates = insuranceOutput.feesAndRates

  // Standalone ex-tax markup from the overcharge rate, before any discount is applied
  if (options?.type === 'overchargeAmount') {
    if (applicableFeesAndRates.orus?.overchargeRate) {
      return multiplyByRate(insuranceOutput.yearlyExTax, applicableFeesAndRates.orus.overchargeRate)
    }
    return zeroAmount
  }

  //We apply the orus overcharge rate to the ex tax amount
  if (applicableFeesAndRates.orus?.overchargeRate) {
    amount = applyRateToAmount(amount, applicableFeesAndRates.orus.overchargeRate)
  }

  //We apply the discount rate to the amount
  if (applicableFeesAndRates.orus?.discountRate) {
    amount = multiplyByRate(
      amount,
      newFinancialRate(1 - financialRateToNumber(applicableFeesAndRates.orus.discountRate)),
    )
  }

  if (options?.type === 'insuredExTax') {
    return amount
  }

  if (options?.type === 'taxes') {
    if (insuranceOutput.taxRate) {
      return multiplyByRate(amount, insuranceOutput.taxRate)
    } else {
      return zeroAmount
    }
  }

  //We apply the tax rate to the amount
  if (insuranceOutput.taxRate) {
    amount = applyRateToAmount(amount, insuranceOutput.taxRate)
  }

  //We apply the fees to the amount
  //#region We apply the orus fees to the amount
  if (options?.type === 'orusInstallmentFee') {
    if (applicableFeesAndRates.orus?.installmentFeeRate) {
      return multiplyByRate(amount, applicableFeesAndRates.orus.installmentFeeRate)
    } else {
      return zeroAmount
    }
  }

  if (applicableFeesAndRates.orus?.installmentFeeRate) {
    amount = applyRateToAmount(amount, applicableFeesAndRates.orus.installmentFeeRate)
  }

  if (options?.type === 'orusManagementFee') {
    if (applicableFeesAndRates.orus?.managementFeeRate) {
      return multiplyByRate(amount, applicableFeesAndRates.orus.managementFeeRate)
    } else {
      return zeroAmount
    }
  }

  if (applicableFeesAndRates.orus?.managementFeeRate) {
    amount = applyRateToAmount(amount, applicableFeesAndRates.orus.managementFeeRate)
  }

  //We apply the discount rate to the insured amount with fees
  if (options?.type === 'orusDiscountAmount') {
    if (applicableFeesAndRates.orus?.discountV2Rate) {
      return multiplyByRate(amount, applicableFeesAndRates.orus?.discountV2Rate)
    } else {
      return zeroAmount
    }
  }
  if (applicableFeesAndRates.orus?.discountV2Rate) {
    amount = multiplyByRate(
      amount,
      newFinancialRate(1 - financialRateToNumber(applicableFeesAndRates.orus.discountV2Rate)),
    )
  }

  //#endregion

  //#region We apply the partner fees to the amount
  if (options?.type === 'partnerManagementFee') {
    if (applicableFeesAndRates.partner?.managementFeeRate) {
      return multiplyByRate(amount, applicableFeesAndRates.partner.managementFeeRate)
    } else {
      return zeroAmount
    }
  }

  if (applicableFeesAndRates.partner) {
    if (applicableFeesAndRates.partner.managementFeeRate) {
      amount = applyRateToAmount(amount, applicableFeesAndRates.partner.managementFeeRate)
    }
  }
  //#endregion
  return amount
}
